import {
  IconCheck,
  IconChevronRight,
  IconRefresh,
  IconX,
} from "@tabler/icons-react";
import { useEffect, useRef, useState, type FC, type ReactNode } from "react";
import {
  Button,
  Disclosure,
  DisclosurePanel,
  Heading,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

/**
 * Normalized status for a tool call, framework-neutral replacement for
 * assistant-ui's `ToolCallMessagePartStatus`. Derived once by `ChatMessage`
 * from the AI SDK tool part's `state` so individual tool components don't have
 * to reason about the full AI SDK state machine.
 */
export type ChatToolStatus =
  | { type: "running" }
  | { type: "complete" }
  | { type: "incomplete" };

/**
 * Props passed to a tool renderer. Replacement for assistant-ui's
 * `ToolCallMessagePartComponent` props:
 * - `input`  (was `args`)
 * - `output` (was `result`)
 * - `status` (same `{ type }` shape as before)
 */
export interface ToolComponentProps<Input = unknown, Output = unknown> {
  toolName: string;
  toolCallId: string;
  input: Input;
  output: Output | undefined;
  errorText: string | undefined;
  status: ChatToolStatus;
}

// Tool renderers are registered in heterogeneous maps keyed by tool name, so
// the input/output types necessarily vary per entry; `any` defaults keep those
// maps assignable to `Record<string, ToolComponent>`.
export type ToolComponent<Input = any, Output = any> = FC<
  ToolComponentProps<Input, Output>
>;

/**
 * The status icon for a tool call (spinner / check / cross). Exported so other
 * tool-call-style rows can render the same icon without the
 * {@link ChatToolBase} wrapper.
 */
export const ChatToolStatusIcon: FC<{ status: ChatToolStatus }> = ({
  status,
}) => {
  if (status.type === "running") {
    return <IconRefresh className="size-3.5 shrink-0 animate-spin" />;
  }
  if (status.type === "complete") {
    return <IconCheck className="size-3.5 shrink-0 text-green-500" />;
  }
  if (status.type === "incomplete") {
    return <IconX className="size-3.5 shrink-0 text-red-500" />;
  }
  return null;
};

/**
 * Lines tool rows and the thinking indicator up with unpadded text, for
 * surfaces on the `agent` message layout. Set on an ancestor; by default rows
 * are inset to align with text inside a padded bubble.
 */
export const toolRowsClassName = "[--chat-tool-inset:0px]";

/**
 * Compact status line shown for a tool call (spinner / check / cross + label).
 * Moved from the admin app's `baseTool.tsx` so every chat surface renders tool
 * progress identically.
 */
export const ChatToolBase: FC<{
  status: ChatToolStatus;
  children: ReactNode;
}> = ({ status, children }) => (
  <div className="flex max-w-full items-center gap-1.5 pl-[var(--chat-tool-inset,--spacing(3.5))] text-xs font-medium text-muted-foreground">
    <ChatToolStatusIcon status={status} />
    <div className="truncate">{children}</div>
  </div>
);

export interface ChatToolDisclosureProps {
  status: ChatToolStatus;
  /** Icon between the chevron and the label; defaults to the status icon. */
  icon?: ReactNode;
  /** The status line's label, as {@link ChatToolBase}'s children. */
  label: ReactNode;
  /** The call's detail, revealed when the row is expanded. */
  children: ReactNode;
  /** Starts expanded when first shown still running (default true). */
  expandsWhileRunning?: boolean;
}

/**
 * An expandable tool call: the same compact status line as {@link ChatToolBase}
 * — leading icon aligned with text inside chat bubbles, so expandable and plain
 * rows read as one column — with a trailing chevron that expands to reveal the
 * call's detail (a result breakdown, a nested agent transcript) along a left
 * border under the icon column. Unless `expandsWhileRunning` is false, it
 * expands the first time it appears while still running and collapses once it
 * completes on screen, while staying manually toggleable.
 */
export const ChatToolDisclosure: FC<ChatToolDisclosureProps> = (props) => {
  const { status, icon, label, children, expandsWhileRunning = true } = props;
  const isComplete = status.type === "complete";
  const [isExpanded, setIsExpanded] = useState(
    expandsWhileRunning && !isComplete,
  );
  const wasComplete = useRef(isComplete);

  useEffect(() => {
    // Collapse a call that was in progress once it finishes while on screen.
    if (isComplete && !wasComplete.current) {
      setIsExpanded(false);
    }
    wasComplete.current = isComplete;
  }, [isComplete]);

  return (
    <Disclosure
      className="pl-[var(--chat-tool-inset,--spacing(3.5))]"
      isExpanded={isExpanded}
      onExpandedChange={setIsExpanded}
    >
      <Heading>
        <Button
          slot="trigger"
          className="flex max-w-full items-start gap-1.5 text-left text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          {icon ?? (
            <span className="mt-0.5 shrink-0">
              <ChatToolStatusIcon status={status} />
            </span>
          )}
          <span className="line-clamp-3">{label}</span>
          {/* Rotated off this disclosure's own state, not a `group` variant —
              nested disclosures would match an expanded ancestor's group. */}
          <IconChevronRight
            className={twMerge(
              "mt-0.5 size-3.5 shrink-0 transition-transform duration-200",
              isExpanded && "rotate-90",
            )}
          />
        </Button>
      </Heading>
      <DisclosurePanel>
        <div className="mt-3 border-l-2 border-border-light pl-3">
          {children}
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
};

export interface ChatToolGroupProps {
  /** The group's tool calls, in order. */
  tools: readonly ToolComponentProps[];
  /** True while any part in the group is producing content. */
  isActive: boolean;
  /** Extra clause after the step count, e.g. which tools ran. */
  detail?: string;
  /** The calls, rendered as they would be ungrouped. */
  children: ReactNode;
}

/**
 * A run of tool calls folded into one collapsed row, which expands to the calls
 * as they render on their own. The row shows as running while any part in it
 * is active, since the collapsed row hides that part's own indicator.
 */
export const ChatToolGroup: FC<ChatToolGroupProps> = (props) => {
  const { tools, isActive, detail, children } = props;
  const status = resolveToolGroupStatus(tools, isActive);
  const failedCount = tools.filter(
    (tool) => tool.status.type === "incomplete",
  ).length;

  const clauses = [describeToolGroupProgress(status, tools.length)];
  if (detail) {
    clauses.push(detail);
  }
  if (failedCount > 0) {
    clauses.push(`${failedCount} failed`);
  }

  return (
    <ChatToolDisclosure
      status={status}
      label={clauses.join(" · ")}
      expandsWhileRunning={false}
    >
      <div className="space-y-3">{children}</div>
    </ChatToolDisclosure>
  );
};

function resolveToolGroupStatus(
  tools: readonly ToolComponentProps[],
  isActive: boolean,
): ChatToolStatus {
  if (isActive) {
    return { type: "running" };
  }
  if (tools.some((tool) => tool.status.type === "incomplete")) {
    return { type: "incomplete" };
  }
  return { type: "complete" };
}

function describeToolGroupProgress(
  status: ChatToolStatus,
  stepCount: number,
): string {
  switch (status.type) {
    case "running":
      return `Working · ${stepCount} steps`;
    case "complete":
    case "incomplete":
      return `Completed ${stepCount} steps`;
  }
}
